import React, { useContext } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Form, Row, Col, ColProps } from "react-bootstrap";
import { useFormContext, Controller } from "react-hook-form";

import {
	Button,
	Skeleton,
	Tooltip,
	RequiredMark,
	SelectAsync,
} from "components";
import styles from "components/select/selectAsync.module.scss";
import { DebounceTimeout } from "globalConstants";
import { SaveInProgressWrapperContext } from "helpers";

import { ErrorMessage } from "./errorMessage";

const defaultProps = {
	labelSize: { sm: 6 },
	controlSize: { sm: 6 },
};

type HookSelectAsyncFormGroupProps = {
	name: string;
	label?: string;
	loadOptions: any;
	disableSkeleton?: boolean;
	options?: any[];
	blurLoading?: boolean;
	defaultValue?: any;
	preselect?: any;
	placeholder?: string;
	disabled?: boolean;
	labelSize?: ColProps;
	controlSize?: ColProps;
	containerClassName?: string;
	containerSelectClassName?: string;
	getOptionLabel?: any;
	getOptionValue?: any;
	debounceTimeout?: number;
	cacheOptions?: boolean;
	isSearchable?: boolean;
	defaultOptions?: boolean;
	additional?: any;
	onChange?: any;
	onIconClick?: any;
	showRequiredMark?: boolean;
	rules?: any;
	icon?: any;
	tooltipTitle?: string;
	isOptionDisabled?: any;
	components?: any;
	hideLabel?: boolean;
};

const HookSelectAsyncFormGroup = ({
	name,
	label,
	loadOptions,
	disableSkeleton = false,
	options = [],
	blurLoading = false,
	defaultValue,
	preselect = {},
	placeholder = "Please Select",
	disabled = false,
	labelSize = defaultProps.labelSize,
	controlSize = defaultProps.controlSize,
	containerClassName = "",
	containerSelectClassName = "",
	getOptionLabel,
	getOptionValue,
	onChange,
	onIconClick,
	showRequiredMark = false,
	rules,
	icon = undefined,
	tooltipTitle = undefined,
	hideLabel = false,
	...props
}: HookSelectAsyncFormGroupProps) => {
	const {
		control,

		formState: { errors },
	} = useFormContext();
	const { handleSave } = useContext(SaveInProgressWrapperContext);

	const showLoader =
		!disableSkeleton &&
		(blurLoading ||
			(blurLoading === undefined && !loadOptions && !options?.length));

	const errorMessage = fp.get(name, errors) && (
		<ErrorMessage field={name} errors={errors} />
	);

	const handleOnClick = _e => {
		if (disabled) return;
		onIconClick?.();
	};

	return (
		<Form.Group
			as={Row}
			className={clsx(styles.selectFormGroup, containerClassName)}
		>
			{!hideLabel && (
				<Form.Label
					column
					className={styles.selectLabel}
					{...labelSize}
				>
					{label}
					{showRequiredMark && <RequiredMark />}
				</Form.Label>
			)}
			<Col {...controlSize} className={clsx(containerSelectClassName)}>
				{showLoader ? (
					<Skeleton height="34px" />
				) : (
					<div className={styles.selectHolder}>
						<Controller
							name={name}
							rules={rules}
							control={control}
							defaultValue={defaultValue}
							render={({ field }) => {
								return (
									<Tooltip
										title={tooltipTitle}
										disabled={!tooltipTitle}
									>
										<SelectAsync
											loadOptions={loadOptions}
											debounceTimeout={DebounceTimeout}
											additional={{ page: 0 }}
											cacheOptions
											hasError={!!errorMessage}
											placeholder={placeholder}
											isDisabled={disabled}
											getOptionLabel={getOptionLabel}
											getOptionValue={getOptionValue}
											// On review
											{...props}
											{...field}
											onChange={(value, { action }) => {
												field?.onChange(value);
												onChange?.(value, {
													action,
												});
											}}
											onBlur={() => handleSave?.()}
										/>
									</Tooltip>
								);
							}}
						/>
						{icon && (
							<Button
								onClick={handleOnClick}
								icon={icon}
								btnIcon
								variant="secondary"
								title=""
								disabled={disabled}
								className={styles.selectIcon}
							/>
						)}
					</div>
				)}
				{!disabled && errorMessage}
			</Col>
		</Form.Group>
	);
};

export default HookSelectAsyncFormGroup;
